import { useContext, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Textarea, Typography } from '@mui/joy';
import { StudioModal, StudioDialog } from '../Workspace/Workspace';
import { LayoutContext } from '../../context';
import { APIManualCompletionPreview, APIManualCompleteEvent } from '../../api';
import { hasAdditional } from './additionalActionsUi';

export default function ManualCompleteEventButton({ event, token, disabled, onChanged }) {
  const { account } = useContext(LayoutContext);
  const positions = Array.isArray(account?.position)
    ? account.position
    : String(account?.position || '').split(',');
  const canManage =
    account?.role === 'administrator' ||
    positions.some((p) => ['manager', 'head_manager'].includes(p.trim())) ||
    (account?.role === 'manager' && !positions.some(Boolean));
  const [open, setOpen] = useState(false),
    [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false),
    [info, setInfo] = useState(null);
  const [reason, setReason] = useState(''),
    [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const generation = useRef(0),
    flight = useRef(null);
  useEffect(() => {
    const run = ++generation.current;
    if (!open) return;
    setLoading(true);
    setInfo(null);
    setError('');
    APIManualCompletionPreview(event.id, token)
      .then((result) => {
        if (run !== generation.current) return;
        if (result?.status === 'ok' && result.revision) setInfo(result);
        else setError(result?.message || 'Не удалось загрузить состояние события');
      })
      .catch(() => {
        if (run === generation.current) setError('Ошибка связи. Обновите данные.');
      })
      .finally(() => {
        if (run === generation.current) setLoading(false);
      });
    return () => {
      generation.current += 1;
    };
  }, [open, event?.id, token, revision]);
  useEffect(() => {
    setOpen(false);
    setReason('');
    setBusy(false);
    flight.current = null;
  }, [event?.id]);

  const confirm = async () => {
    if (flight.current !== null || !info || info.blocker || reason.trim().length < 5 || reason.length > 4000)
      return;
    const run = generation.current;
    flight.current = run;
    setBusy(true);
    setError('');
    try {
      const result = await APIManualCompleteEvent(
        event.id,
        {
          expected_revision: info.revision,
          reason: reason.trim(),
        },
        token,
      );
      if (run !== generation.current) return;
      if (result?.status !== 'ok') setError(result?.message || 'Не удалось завершить событие');
      else {
        setOpen(false);
        onChanged?.(result.event);
        window.dispatchEvent(new Event('surveyor-additional-changed'));
      }
    } catch (_) {
      if (run === generation.current) setError('Ошибка связи. Обновите данные перед повтором.');
    } finally {
      if (flight.current === run) flight.current = null;
      if (run === generation.current) setBusy(false);
    }
  };
  if (
    !canManage ||
    !event?.id ||
    (event.kanban_stage === 'completed' && !hasAdditional(event) && !event.is_rework)
  )
    return null;
  return (
    <>
      <Button
        variant="outlined"
        color="warning"
        disabled={disabled || busy}
        onClick={() => {
          setReason('');
          setError('');
          setBusy(false);
          setOpen(true);
        }}
      >
        Завершить вручную
      </Button>
      <StudioModal
        open={open}
        onClose={() => {
          if (!busy) setOpen(false);
        }}
      >
        <StudioDialog sx={{ width: { xs: '100%', sm: 520 }, gap: 2 }}>
          <Typography component="h2" level="title-lg">
            Завершить событие вручную
          </Typography>
          <Typography level="body-sm">
            Событие №{info?.deal_no || event.bitrix_deal?.id || event.id}
          </Typography>
          <Alert color="warning">
            Для старых или ошибочно оставленных в работе событий. Карточка перейдёт в «Завершён», ненужные
            дозамеры будут сняты. Это действие не заменяет проверку качества и выдачу материалов клиенту.
          </Alert>
          {loading ? (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <CircularProgress aria-label="Загрузка состояния события" />
            </Box>
          ) : (
            info && (
              <>
                <Typography level="body-sm">
                  Выплаты, назначения и материалы сохранятся. Новые задания, акты и платежи автоматически не
                  создаются.
                </Typography>
                <Typography level="body-sm">
                  {info.date_completed
                    ? `Прежняя дата завершения сохранится: ${new Date(info.date_completed).toLocaleString('ru-RU')}.`
                    : 'Дата завершения ещё не указана — будет установлена текущая дата.'}
                </Typography>
                {(info.active_cycles > 0 || info.legacy_additional) && (
                  <Typography level="body-sm">
                    {info.active_cycles
                      ? `Будет снято дозамеров: ${info.active_cycles}. История и файлы останутся.`
                      : 'Будет снят старый признак дозамера без записи цикла.'}
                  </Typography>
                )}
                {info.pending_uploads > 0 && !info.blocker && (
                  <Alert color="warning">
                    Ожидающие загрузки будут остановлены. Уже загруженные файлы сохранятся.
                  </Alert>
                )}
                {info.blocker && <Alert color="danger">{info.blocker}</Alert>}
              </>
            )
          )}
          <Textarea
            minRows={3}
            value={reason}
            disabled={busy}
            aria-label="Причина ручного завершения"
            placeholder="Почему событие нужно завершить вручную"
            onChange={(e) => setReason(e.target.value)}
          />
          {error && <Alert color="danger">{error}</Alert>}
          {(error || info?.blocker) && (
            <Button variant="plain" disabled={busy || loading} onClick={() => setRevision((v) => v + 1)}>
              Обновить данные
            </Button>
          )}
          <Stack
            direction={{ xs: 'column-reverse', sm: 'row' }}
            gap={1}
            justifyContent="flex-end"
            sx={{ mt: 'auto' }}
          >
            <Button variant="outlined" color="neutral" disabled={busy} onClick={() => setOpen(false)}>
              Отмена
            </Button>
            <Button
              color="warning"
              loading={busy}
              disabled={
                loading || !info || !!info.blocker || reason.trim().length < 5 || reason.length > 4000
              }
              onClick={confirm}
            >
              Завершить вручную
            </Button>
          </Stack>
        </StudioDialog>
      </StudioModal>
    </>
  );
}
